import { useEffect, useRef, useState } from "react";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide, SwiperClass } from "swiper/react";
import { postOfferView } from "../../../../services/offers/offers.service";
import {
  CampaignType,
  Offer,
  OffersIdSlots,
  PlacementStage,
  QueryPostOfferView,
  Template,
  TemplateOptionSettings,
} from "../../../../types/global.types";
import OfferItem from "../offer-item/OfferItem.component";
import { getSettledSlideDirection } from "../../../../utility/swiperSlideTransition";

interface Props {
  settings: TemplateOptionSettings;
  settingsTemplate: Template;
  campaign: CampaignType;
  displayOnly: boolean;
  campaignId: string;
  placementId: string;
  placementName: string;
  emailSentIds: string[];
  isSeen: boolean;
  preEmail?: string;
  setPreEmail?: (email: string) => void;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  layoutId: string;
}

const SwiperComponent = ({
  settings,
  emailSentIds,
  campaign,
  displayOnly,
  campaignId,
  placementId,
  placementName,
  settingsTemplate,
  openSingle,
  onAdd,
  isSeen,
  preEmail,
  setPreEmail,
  layoutId,
}: Props) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [eventOfferIds, setEventOfferIds] = useState<OffersIdSlots[]>([]);
  // iframe width - 80px of slider arrows and 70px of padding
  const swiperWidth = document.body.offsetWidth - 110;

  // swiper item that are fully displayed with margin 20px
  const swiperItemDisplayed = Math.floor(swiperWidth / 370);

  // swiper item that are fully displayed with last item without margin
  const swiperSize = Math.floor((swiperWidth - swiperItemDisplayed * 20) / 350);

  // swiper item that are half displayed with margin 20px
  const swiperItemForLoop = Math.ceil(swiperWidth / 370);

  const refNavPrev = useRef<HTMLDivElement | null>(null);
  const refNavNext = useRef<HTMLDivElement | null>(null);
  const lastTrackedRealIndexRef = useRef(0);

  const [noNavigation, setNoNav] = useState<boolean>(
    refNavNext.current?.classList.contains("swiper-button-lock") || false
  );

  const setDefaultOfferImpressionEvent = async (offers: OffersIdSlots[]) => {
    if (eventOfferIds.length < campaign.offers.length) {
      const data: QueryPostOfferView = {
        offers,
        campaignId,
        campaignName: campaign.name || "",
        companyId: campaign.company_Id,
        placementId,
        placementName,
        pageUrl: "",
        templateId: settingsTemplate.identifier,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        layoutId: layoutId,
      };

      await postOfferView(data);
    }
  };

  //set event for default active slide offers
  useEffect(() => {
    if (refNavNext.current && isSeen) {
      const hasLock =
        refNavNext.current.classList.contains("swiper-button-lock");
      const swiperWidth = document.body.offsetWidth - (hasLock ? 40 : 80);
      const swiperItemDisplayed = Math.floor(swiperWidth / 370);
      setNoNav(hasLock);

      if (swiper) {
        const _ids = campaign.offers
          .map((offer: Offer, index: number) => ({
            id: offer.id,
            slot: index + 1,
          }))
          .slice(0, swiperItemDisplayed !== 0 ? swiperItemDisplayed : 1);
        setDefaultOfferImpressionEvent(_ids);
        setEventOfferIds(_ids);
      }
    }
    // eslint-disable-next-line
  }, [swiper, isSeen]);

  const trackSlideImpressionOnPrev = (activeIndex: number) => {
    const offersLength = campaign.offers.length;
    if (offersLength >= activeIndex - 1) {
      const offerIds = eventOfferIds.map((value: OffersIdSlots) => value.id);
      if (!offerIds.includes(campaign.offers[activeIndex].id)) {
        setDefaultOfferImpressionEvent([
          {
            id: campaign.offers[activeIndex].id,
            slot: activeIndex + 1,
          },
        ]);
        setEventOfferIds((prevState) => [
          ...prevState,
          {
            id: campaign.offers[activeIndex].id,
            slot: activeIndex + 1,
          },
        ]);
      }
    }
  };

  const trackSlideImpressionOnNext = (activeIndex: number) => {
    const offersLength = campaign.offers.length;
    const swiperSizeNoZero = swiperSize !== 0 ? swiperSize : 1;

    if (activeIndex === offersLength - 1) {
      const offerIds = eventOfferIds.map((value: OffersIdSlots) => value.id);
      if (!offerIds.includes(campaign.offers[0].id)) {
        setDefaultOfferImpressionEvent([
          { id: campaign.offers[0].id, slot: 1 },
        ]);
        setEventOfferIds((prevState) => [
          ...prevState,
          { id: campaign.offers[0].id, slot: 1 },
        ]);
      }
    }

    if (offersLength > activeIndex + swiperSizeNoZero - 1) {
      const offerIds = eventOfferIds.map((value: OffersIdSlots) => value.id);
      if (
        !offerIds.includes(
          campaign.offers[activeIndex + swiperSizeNoZero - 1].id
        )
      ) {
        setDefaultOfferImpressionEvent([
          {
            id: campaign.offers[activeIndex + swiperSizeNoZero - 1].id,
            slot: activeIndex + swiperSizeNoZero,
          },
        ]);
        setEventOfferIds((prevState) => [
          ...prevState,
          {
            id: campaign.offers[activeIndex + swiperSizeNoZero - 1].id,
            slot: activeIndex + swiperSizeNoZero,
          },
        ]);
      }
    }
  };

  return (
    <>
      {campaign?.offers.length > 0 && (
        <div
          className={`inline-swiper-wrapper ${
            noNavigation ? "no-navigation" : ""
          }`}
        >
          <Swiper
            className={`swiper-container swiper-inline`}
            modules={[Navigation]}
            slidesPerView={"auto"}
            spaceBetween={20}
            loopAddBlankSlides={true}
            loop={swiperItemForLoop < campaign.offers.length}
            onSwiper={(s: SwiperClass) => {
              setSwiper(s);
              lastTrackedRealIndexRef.current = s.realIndex;
            }}
            navigation={{
              prevEl: refNavPrev.current,
              nextEl: refNavNext.current,
            }}
            onBeforeInit={(swiper: any) => {
              swiper.params.navigation.prevEl = refNavPrev.current;
              swiper.params.navigation.nextEl = refNavNext.current;
            }}
            onTransitionEnd={(_swiper: SwiperClass) => {
              const direction = getSettledSlideDirection(
                _swiper,
                lastTrackedRealIndexRef.current
              );
              if (direction === null) return;
              const activeIndex = _swiper.realIndex;
              lastTrackedRealIndexRef.current = activeIndex;
              if (direction === "next") {
                trackSlideImpressionOnNext(activeIndex);
              } else {
                trackSlideImpressionOnPrev(activeIndex);
              }
            }}
          >
            {campaign?.offers.map((o: Offer, i: number) => {
              return (
                <SwiperSlide
                  key={i}
                  style={{ color: settings?.dealLayout.textColor }}
                >
                  <OfferItem
                    offer={o}
                    slot={i + 1}
                    emailSentIds={emailSentIds}
                    settings={settings.dealLayout}
                    isAdded={o.isAdded || false}
                    settingsTemplate={settingsTemplate}
                    onAdd={onAdd}
                    displayOnly={displayOnly}
                    campaign={campaign}
                    retailerId={campaign?.company_Id}
                    campaignId={campaignId}
                    placementId={placementId}
                    placementName={placementName}
                    openSingle={openSingle}
                    hasVoucher={campaign.offers.some(
                      (offer) => offer.voucher && offer.voucher.voucherType
                    )}
                    otherSettings={settings.otherSettings}
                    preEmail={preEmail}
                    setPreEmail={setPreEmail}
                    layoutId={layoutId}
                    layoutOptions={settings.layoutOptions}
                  />
                </SwiperSlide>
              );
            })}
            <div className="swiper-button-prev" ref={refNavPrev}>
              <svg
                width="34"
                height="34"
                viewBox="0 0 34 34"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="17"
                  cy="17"
                  r="17"
                  transform="rotate(-180 17 17)"
                  fill={settings.dealLayout.backgroundColor}
                />
                <rect
                  x="18.6094"
                  y="23"
                  width="8.6192"
                  height="1.72384"
                  rx="0.86192"
                  transform="rotate(-135 18.6094 23)"
                  fill={settings.dealLayout.textColor}
                />
                <rect
                  x="20.3135"
                  y="12.2188"
                  width="8.6192"
                  height="1.72384"
                  rx="0.86192"
                  transform="rotate(135 20.3135 12.2188)"
                  fill={settings.dealLayout.textColor}
                />
              </svg>
            </div>
            <div className="swiper-button-next" ref={refNavNext}>
              <svg
                width="34"
                height="34"
                viewBox="0 0 34 34"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="17"
                  cy="17"
                  r="17"
                  fill={settings.dealLayout.backgroundColor}
                />
                <rect
                  x="15.3906"
                  y="11"
                  width="8.6192"
                  height="1.72384"
                  rx="0.86192"
                  transform="rotate(45 15.3906 11)"
                  fill={settings.dealLayout.textColor}
                />
                <rect
                  x="14.1719"
                  y="22.2656"
                  width="8.6192"
                  height="1.72384"
                  rx="0.86192"
                  transform="rotate(-45 14.1719 22.2656)"
                  fill={settings.dealLayout.textColor}
                />
              </svg>
            </div>
          </Swiper>
        </div>
      )}
    </>
  );
};

export default SwiperComponent;
